"use client";

import React, {
  MutableRefObject,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import QueryString from "query-string";
import { usePathname, useSearchParams } from "next/navigation";
import styled from "styled-components";
import {
  ActivityIndicator,
  Box,
  Kit,
  KitVersion,
  Section,
  Item,
  Text,
  PagingIndicator,
  useNavigation,
  buildURL,
  EmptyState,
  Redirect,
  useBoolean,
} from "@thenounproject/lingo-core";
import Head from "next/head";

import GalleryItems from "./GalleryItems";
import { scrollToItem } from "@helpers/items";
import { InlineInput, InputContainer } from "@components/common/InlineEditableField";

import type { RootState } from "@redux/store";
import { getSection } from "@selectors/getters";
import SectionFooterNav from "@features/navigation/SectionFooterNav";
import { useAppSelectorV1, useAppDispatchV1 } from "@redux/hooks";

import useBatchUpdateItemStatus from "@actions/items/useBatchUpdateItemStatus";
import useSectionItems from "@redux/actions/items/useSectionItems";
import useUpdateItem from "@redux/actions/items/useUpdateItem";
import useNavPoint from "@hooks/useNavPoint";
import useHash from "../../../app/_lib/hooks/useHash";
import { captureMessage } from "@sentry/nextjs";
import { refreshNavPoint } from "@redux/actions/useRefreshNavPoint";
import { NavPointTypes } from "@redux/navPoint/NavPoint";
import useUpdateSection from "@redux/actions/sections/useUpdateSection";

const FetchingIndicatorWrapper = styled(Box).attrs({
  background: "rgba(255, 255, 255, 0.8)",
  position: "fixed",
  top: 61,
  bottom: 0,
  right: 0,
  height: "100%",
  zIndex: 1,
})``;

//
export const SectionTitle = styled(Text).attrs({
  as: "h1",
  font: "gallery.title",
  mb: "xl",
  px: "s",
})`
  cursor: pointer;
`;

type Props = {
  section: Section;
  kit: Kit;
  kitVersion: KitVersion;
  getItemUrl: (item: Item) => string;
  canEditContent: boolean;
  selectedItems: string[];
  onSelectItem: (event: React.MouseEvent, itemId: string, allItems: Item[]) => void;
  setSelectedItems: (itemIds: string[]) => void;
  scrollContainerRef: MutableRefObject<HTMLDivElement>;
};

const SectionComponent: React.FC<Props> = ({
  section,
  kit,
  kitVersion,
  getItemUrl,
  canEditContent,
  selectedItems,
  onSelectItem,
  setSelectedItems,
  scrollContainerRef,
}) => {
  const navigate = useNavigation();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const hash = useHash();
  const navPoint = useNavPoint();
  const { portal } = navPoint;
  const dispatch = useAppDispatchV1();

  const currentSection = navPoint.section || section;

  const [batchUpdateItemStatus] = useBatchUpdateItemStatus();

  const [updateItem] = useUpdateItem();
  const [updateSection] = useUpdateSection();

  const [editing, setEditing, setNotEditing] = useBoolean(false);
  const [editValue, setEditValue] = useState<string>(currentSection.name || "Untitled page");
  const inputRef = useRef<HTMLTextAreaElement>(null);

  const {
    data: { items, total } = {},
    isLoading,
    error: loadingError,
    fetchNextPage,
  } = useSectionItems({
    sectionId: currentSection.id,
    version: currentSection.version,
  });
  const hasMore = items?.length >= total ? false : true;

  // keep editValue synchronized with currentSection.name
  useEffect(() => {
    const nextValue = currentSection.name || "Untitled page";
    setEditValue(prev => (prev === nextValue ? prev : nextValue));
  }, [currentSection.name]);

  // Ensure navPoint is updated when returning to section from Asset Detail overlay.
  useEffect(() => {
    const isOnSectionPage =
      (pathname.includes(currentSection.shortId) || pathname.includes(currentSection.id)) &&
      !pathname.includes("/a/");

    // If navPoint thinks we're on an Item but we're actually on Section, fix it
    if (isOnSectionPage && navPoint.type === NavPointTypes.Item) {
      dispatch(
        refreshNavPoint({
          ...navPoint,
          type: NavPointTypes.Section,
          itemId: undefined,
          item: undefined,
        })
      );
    }
  }, [pathname, currentSection, navPoint, dispatch]);

  useEffect(() => {
    if (editing && inputRef.current) {
      inputRef.current.select();
    }
  }, [editing]);

  // Automatically begin editing section title when navigating from new kit creation
  useEffect(() => {
    const shouldEdit = searchParams.get("edit_section");
    if (shouldEdit === "true" && canEditContent && !editing) {
      setEditing();

      // Clean up the URL parameter
      const newSearchParams = new URLSearchParams(searchParams.toString());
      newSearchParams.delete("edit_section");
      const newUrl = `${pathname}${newSearchParams.toString() ? `?${newSearchParams.toString()}` : ""}`;
      navigate.replace(newUrl);
    }
  }, [searchParams, canEditContent, setEditing, navigate, pathname, editing]);

  // Exit edit mode if permissions change and editing is no longer allowed
  useEffect(() => {
    if (!canEditContent && editing) {
      setNotEditing();
    }
  }, [canEditContent, editing, setNotEditing]);

  const onDoubleClickTitle = useCallback(() => {
    if (!canEditContent) return;
    setEditing();
  }, [canEditContent, setEditing]);

  const onEditSection = useCallback(() => {
    const trimmedValue = editValue.trim();
    // If the user entered only whitespace, reset to the original name and exit edit mode
    if (trimmedValue === "") {
      setEditValue(currentSection.name);
      setNotEditing();
      return;
    }
    // If nothing has changed, just exit edit mode
    if (editValue === currentSection.name) {
      setNotEditing();
      return;
    }
    void updateSection({ sectionId: currentSection.id, updates: { name: editValue } });
    setNotEditing();
  }, [editValue, currentSection.id, currentSection.name, setNotEditing, updateSection]);

  // TODO: Possibly refactor this
  // This was a quick fix to replace mapStateToProps with useSelector
  const { space, nextSection, prevSection, totalSections, currentSectionIndex } = useAppSelectorV1(
    (state: RootState) => {
      const versionId = `${currentSection.id}-${currentSection.version}`,
        space = state.entities.spaces.objects[kit.spaceId];

      let nextSection = null;
      let prevSection = null;
      let currentSectionIndex = 0;
      const totalSections =
        kitVersion?.sections.reduce((count, secId) => {
          const sec = getSection(state, { identifier: secId, version: kitVersion.version });
          return sec?.isSeparator ? count : count + 1;
        }, 0) || 0;

      if (kitVersion && kitVersion.sections && kitVersion.sections.length > 1) {
        currentSectionIndex = kitVersion.sections.indexOf(versionId);

        for (let i = currentSectionIndex + 1; i < kitVersion.sections.length; i++) {
          const sec = getSection(state, {
            identifier: kitVersion.sections[i],
            version: kitVersion.version,
          });
          if (sec?.isSeparator) continue;
          nextSection = {
            path: buildURL(
              `/s/${sec.shortId}/`,
              { space, portal },
              { kit_token: kit.shareToken || undefined, v: kitVersion.version }
            ),
            name: sec.name,
          };
          break;
        }

        for (let i = currentSectionIndex - 1; i >= 0; i--) {
          const sec = getSection(state, {
            identifier: kitVersion.sections[i],
            version: kitVersion.version,
          });
          if (sec?.isSeparator) continue;
          prevSection = {
            path: buildURL(
              `/s/${sec.shortId}/`,
              { space, portal },
              { kit_token: kit.shareToken || undefined, v: kitVersion.version }
            ),
            name: sec.name,
          };
          break;
        }
      }

      return {
        space: space,
        nextSection,
        prevSection,
        totalSections,
        currentSectionIndex,
      };
    }
  );

  // Scrolling to an item if the URL has a hash
  const headingHash = useMemo(() => {
    // Don't scroll if configuring theme
    if (!hash || hash === "#configure") return;
    // Ignore hashes if the url is not the current page. This can cause issues if we
    // link to another page with a hash since this page may "consume" the hash while
    // the nav point is loading.
    const onPage =
      pathname.includes(currentSection.shortId) || pathname.includes(currentSection.id);
    return onPage ? hash?.replace("#", "") : null;
  }, [hash, pathname, currentSection.id, currentSection.shortId]);

  useLayoutEffect(() => {
    if (!headingHash) return;
    // If we found the hash or there are no more items
    // remove it from the URL so we don't scroll again
    if (scrollToItem(headingHash) || !hasMore) {
      const searchString = searchParams.toString();
      const newUrl = `${pathname}${searchString ? `?${searchString}` : ""}`;
      navigate.replace(newUrl);
    } else if (!isLoading) {
      fetchNextPage();
    }
  }, [fetchNextPage, hasMore, headingHash, navigate, isLoading, pathname, searchParams]);

  if (loadingError?.details?.access === "password") {
    const lingoTokens = document.cookie
      .split("; ")
      .filter(cookie => cookie.startsWith("lingo_tokens"));
    captureMessage("Section access requires password", {
      extra: {
        portalId: portal.id,
        sectionId: currentSection.id,
        lingoTokens,
      },
    });
    const queryString = QueryString.stringify({ next: window.location.toString() });
    return <Redirect to={`/enter-password?${queryString}`} />;
  }

  function renderFetchingIndicator() {
    if (headingHash) {
      const container = scrollContainerRef.current;
      if (!container) return;

      return (
        <FetchingIndicatorWrapper width={"calc(100% - 320px)"} height="100%">
          <ActivityIndicator size="large" center />
        </FetchingIndicatorWrapper>
      );
    }
  }

  const renderTitle = currentSection && kitVersion,
    themeActive = space && space.theme.active,
    docTitle = renderTitle
      ? `${kit.name}: ${currentSection.name}${themeActive ? "" : " on Lingo"}`
      : null;

  function renderSectionTitle() {
    if (!currentSection) return null;

    if (editing) {
      return (
        <form
          onSubmit={e => {
            e.preventDefault();
            onEditSection();
          }}>
          <InputContainer canEdit={canEditContent} isEditing={editing} mb="xl" mx="0" px="s">
            <InlineInput
              ref={inputRef}
              value={editValue}
              onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setEditValue(e.target.value)}
              onBlur={onEditSection}
              autoFocus
              aria-label="Section title"
              font="gallery.title"
              rows={1}
              onKeyDown={e => {
                // No newline allowed in section title
                if (e.key === "Enter") {
                  e.preventDefault();
                  e.currentTarget.blur();
                }
                if (e.key === "Escape") {
                  e.preventDefault();
                  setEditValue(currentSection.name);
                  setNotEditing();
                }
              }}
            />
          </InputContainer>
        </form>
      );
    }

    return (
      <SectionTitle onDoubleClick={onDoubleClickTitle}>
        {currentSection.name || "Untitled page"}
      </SectionTitle>
    );
  }

  function renderContent() {
    if (isLoading && !items?.length) {
      return <ActivityIndicator mt="xxl" center />;
    }
    if (loadingError && !items?.length) {
      return (
        <EmptyState
          title="Unable to load page"
          subtitle={loadingError.message}
          iconProps={{ iconId: "error", fill: "error" }}
          button={{ text: "Retry", onClick: fetchNextPage }}
        />
      );
    } else if (currentSection.status === "pending") {
      return (
        <EmptyState
          title="This lage is being duplicated"
          subtitle="The assets in this page are still processing, check back in a few minutes."
          iconProps={{ iconId: "content.section", size: "90", fill: "gray" }}
        />
      );
    }
    return (
      <>
        <GalleryItems
          section={section}
          items={items}
          selectedItems={selectedItems}
          getItemUrl={getItemUrl}
          onSelectItem={onSelectItem}
          setSelectedItems={setSelectedItems}
          scrollContainerRef={scrollContainerRef}
          updateItem={updateItem}
          batchUpdateItemStatus={batchUpdateItemStatus}
          canEditContent={currentSection.status === "active" && canEditContent}
        />

        <PagingIndicator
          hasMore={hasMore}
          data={items}
          isLoading={isLoading}
          error={loadingError}
          fetchNextPage={fetchNextPage}
        />
      </>
    );
  }

  return (
    <>
      {docTitle ? (
        <Head>
          <title>{docTitle}</title>
        </Head>
      ) : null}
      {renderSectionTitle()}
      {renderFetchingIndicator()}
      {renderContent()}
      <SectionFooterNav {...{ nextSection, prevSection, totalSections, currentSectionIndex }} />
    </>
  );
};

export default SectionComponent;
